import {h} from 'preact';
import {Form, Formik, ErrorMessage} from 'formik';
import invoke from 'lodash.invoke';
import cx from 'classnames';
import assign from 'object-assign';
import appLogger from '@core/LoggingProvider';
import ProgressBar from '../ProgressBar';
import Button from '@components/common/Button';
import AddressBookItem from '@components/account/AddressBook/AddressBookItem';
import RegistrationService from '@services/RegistrationService';
import Icon from '@components/common/Icon';
import Message from '@components/common/Message';
import {formatPhoneUS} from '@util/StringUtils';

const NotValidated = ( props, context ) =>
    <div>

        <ProgressBar step={'3'} completed={[1, 1, 0]} labels={['create login', 'office details', 'verify account']}/>

        <h3 className="text-center md:text-lg font-normal">
            Help us check your shipping address for accuracy by selecting one of the suggested options below
        </h3>

        <div className="universalForm-container my-4 mx-auto">
            <Formik
                initialValues={{
                    addressID: null,
                    loading: false,
                    asIs: {
                        id: 'asIs',
                        address1: context.state.address1,
                        address2: context.state.address2,
                        city: context.state.city,
                        state: context.state.state,
                        postalCode: context.state.postalCode
                    }
                }}
                validate={formData => {
                    const {addressID} = formData;
                    const errors = {};

                    if ( !addressID ) {
                        errors.addressID = 'Please select a default shipping address.';
                    }

                    return errors;
                }}
                onSubmit={( values, {setStatus, setSubmitting} ) => {

                    values.loading = true;
                    const {emailAddress, password, dentalLicense, dentistFullName, practiceName, specialty, officePhone} = context.state;
                    const selectedItem = context.state.addresses.find(
                        ( address, i ) => values.addressID === i.toString() );
                    const {address1, address2, city, state, postalCode, county} = selectedItem || values.asIs;
                    const registerNewCustomerInput = {
                        dentistFullName,
                        practiceName,
                        specialty,
                        officePhone: formatPhoneUS( officePhone ),
                        dentalLicense,
                        officeAddress: {
                            address1,
                            address2,
                            city,
                            state,
                            postalCode,
                            ...(county && {county})
                        },
                        emailAddress,
                        password
                    };

                    if ( values.addressID === 'asIs' ) {
                        appLogger.triggerEvent( {
                            message: 'Registration: New Customer',
                            extra: registerNewCustomerInput
                        } );
                        setSubmitting( false );
                        values.loading = false;
                        setStatus( {serverError: `We could not verify this address, please check your entry or call ${upi.conf.phoneTollFree}`} );
                        return null;
                    }

                    RegistrationService.registerNewCustomer( {input: registerNewCustomerInput} )
                        .then( resp => {
                            const {registerNewCustomer} = resp.data;
                            values.loading = false;
                            if ( registerNewCustomer ) {
                                props.history.push( {
                                    pathname: '/accountVerifiedNPI',
                                    state: {isSubmitted: true, emailAddress}
                                } );
                            }
                            else {
                                props.history.push( {
                                    pathname: '/accountRequiresVerification',
                                    state: {isSubmitted: true, emailAddress}
                                } );
                            }
                        } )
                        .catch( error => {
                            appLogger.capture( error );
                            setSubmitting( false );
                            values.loading = false;
                            console.log( error );
                            setStatus( {serverError: 'There was an error submitting this request, please try again.'} );
                        } );
                }}
                render={( {values, errors, status, touched, handleSubmit, handleChange, isSubmitting} ) => (
                    <Form className="my-4 universalForm">
                        {status &&
                        <Message fail
                                 className="bg-fail flex font-normal items-center -ml-4 -mr-4 md:-ml-8 md:-mr-8 px-4 md:px-8 py-4 text-white w-auto mb-4">
                            <Icon.ui wrapperClassName="mr-2" className="text-white text-xl"
                                     name="report-problem-triangle"/>
                            <span className="message-bar-msg">{status.serverError || status.email}</span>
                        </Message>
                        }
                        <h1>Select the best address: </h1>
                        <ul className="list-reset my-0">
                            {context.state.addresses.map( ( item, i ) => {
                                    const itemId = i.toString();
                                    const isChecked = values.addressID === itemId && true;
                                    const groupId = 'addressID';
                                    const itemProps = assign( {}, item );
                                    return (
                                        <li className="address-item py-4">
                                            <label htmlFor={`${groupId}-${itemId}`}>
                                                <AddressBookItem {...itemProps}>
                                            <span className="wrap-check mr-2">
                                            <input id={`${groupId}-${itemId}`}
                                                   type="radio"
                                                   name={groupId}
                                                   value={itemId}
                                                   onClick={handleChange}/>
                                                   <span className={`bg-white ${cx( 'radio',
                                                       {checked: isChecked} )}`}></span>
                                           </span>
                                                </AddressBookItem>
                                            </label>
                                        </li>
                                    );
                                }
                            )}
                            <li className="py-4">
                                <label htmlFor="leaveAsIs">
                                    <AddressBookItem className="text-sm" address1={values.asIs.address1}
                                                     address2={values.asIs.address2} city={values.asIs.city}
                                                     state={values.asIs.state} postalCode={values.asIs.postalCode}>
                                        <span className="wrap-check mr-2">
                                            <input id="leaveAsIs"
                                                   type="radio"
                                                   name="addressID"
                                                   value="asIs"
                                                   onClick={handleChange}/>
                                                   <span className={`bg-white ${cx( 'radio',
                                                       {checked: values.addressID === 'asIs' && true} )}`}></span>
                                           </span>
                                        <span className="leaveAsIsLabel text-base">Leave my address as-is</span>
                                    </AddressBookItem>
                                </label>
                            </li>
                        </ul>

                        {errors &&
                        <div className="text-fail mb-4">
                            <ErrorMessage className="mb-4" name="addressID"/>
                        </div>}

                        <div className="button-bar flex-row mt-8 mx-0">
                            <span className="button tertiary"
                                  onClick={context.handleGoBack}
                                  style="margin-left: 0 !important;">Back</span>
                            <Button loading={values.loading}
                                    disabled={isSubmitting}
                                    primary
                                    onClick={handleSubmit}
                                    style="margin-right: 0 !important;">Next</Button>
                        </div>
                    </Form>
                )}/>
        </div>
    </div>;

export default NotValidated;